import { breakpoints, Heading, Stack } from '@etg/wings';
import { css, keyframes } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { useEffect, useRef } from 'react';
import type { RebookEligibilityType } from '../../order-details/components/manage-trip/rebooking/models';
import type { RebookingSearchType } from '../models';
import { screenReadersOnlyStyles } from '../../../utils/styleUtils';
import NoResults from './NoResults';
import RebookingTripResult from './RebookingTripResult';

const resultsMoveAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translateY(0);
  }
  10% {
    opacity: 0.5;
    transform: translateY(10px);
  }
  20% {
    opacity: 0.25;
    transform: translateY(20px);
  }
  30% {
    opacity: 0.25;
    transform: translateY(30px);
  }
  40% {
    opacity: 0.25;
    transform: translateY(40px);
  }
  50% {
    opacity: 0;
    transform: translateY(50px);
  }
  60% {
    opacity: 0.25;
    transform: translateY(35px);
  }
  70% {
    opacity: 0.5;
    transform: translateY(15px);
  }
  80% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const flightResultsInnerWrapperStyles = css`
  animation: ${resultsMoveAnimation} 0.8s;
`;

interface AvailableTripResultsProps {
  search: NonNullable<RebookingSearchType>;
  handleFetchMoreFlights: () => Promise<void>;
  isLoadingMoreResults: boolean;
  flightsOffset: number | null;
}

const AvailableTripResults = ({
  search,
  handleFetchMoreFlights,
  isLoadingMoreResults,
  flightsOffset,
}: AvailableTripResultsProps) => {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLHeadingElement | null>(null);
  const resultCardRef = useRef<HTMLHeadingElement | null>(null);

  const { flights, flightsCount, hasAirlineAncillaries, rebookFlow } = search;
  const prevFlightsLength = useRef(flights.length);

  useEffect(() => {
    if (!flightsOffset) {
      headerRef.current?.focus();
      return;
    }

    if (flights.length > prevFlightsLength.current) {
      resultCardRef.current?.focus();
    }
    prevFlightsLength.current = flights.length;
  }, [flightsOffset, flights.length]);

  return (
    <Stack
      spacing={{
        [breakpoints._0]: 16,
        [breakpoints._768]: 24,
      }}
    >
      <Heading level={2} ref={headerRef} tabIndex={-1}>
        {t('ChangeTrip.Results.Title')}
      </Heading>
      <Stack data-testid="changeTrip-searchResults">
        <Stack as="ul" className={flightResultsInnerWrapperStyles}>
          {flights.map((flight, index) => (
            <li key={index}>
              <>
                <Heading
                  className={screenReadersOnlyStyles}
                  level={3}
                  ref={index === flightsOffset ? resultCardRef : undefined}
                  tabIndex={-1}
                >
                  {t(
                    'ScreenReader.ChangeTrip.CurrentResult.Hidden.Text',
                    index + 1,
                    flights.length,
                  )}
                </Heading>
                <RebookingTripResult
                  flight={flight}
                  hasAirlineAncillaries={hasAirlineAncillaries}
                  index={index}
                  key={index}
                  rebookEligibilityType={rebookFlow as RebookEligibilityType}
                />
              </>
            </li>
          ))}
        </Stack>
        <NoResults
          flightResults={flights}
          flightsCount={flightsCount}
          handleFetchMoreFlights={handleFetchMoreFlights}
          isLoadingMoreResults={isLoadingMoreResults}
        />
      </Stack>
    </Stack>
  );
};

export default AvailableTripResults;
